import { executeTool, tools } from './tools';
import type { Provider } from './storage';

const SYSTEM_PROMPT = `You are an AI assistant inside Float:Flash-html5 — a static, browser-only editor for short promo videos and card-news.

Capabilities you have via tools:
- get_project: read current canvas + layers
- add_text_layer / add_shape_layer / update_layer / delete_layer / duplicate_layer / select_layer
- set_background (color, gradient, image, video)
- set_canvas (ratio, safeRatio, duration)

Conventions:
- Coordinates are CANVAS COORDS, center-anchored (x/y is layer center).
- 9:16 canvas is 1080×1920 (center 540, 960). 1:1 is 1080×1080 (center 540, 540).
- Times are in milliseconds.
- For headlines use fontSize 80–160px and fontWeight 800–900. Body text 36–60px.
- Default font family is "Pretendard" — Korean-friendly. You don't need to set it.
- Common animations: pop (impact), fade (subtle), slide (motion), typing (reveal), strike (cross-out).
- Plan one or two layers, call get_project first if you need ids or current state.

When the user asks in Korean, respond in Korean. Keep responses short. After making changes, list what you did in 1–2 lines.`;

export interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

export interface RunOptions {
  provider: Provider;
  apiKey: string;
  /** Display history to render in UI (text only). */
  history: ChatTurn[];
  userMessage: string;
  /** Called whenever the assistant produces a chunk of human-readable text. */
  onText: (text: string) => void;
  /** Called when a tool is invoked (for surface-level UI). */
  onTool: (info: { name: string; ok: boolean; detail?: string }) => void;
  /** Called once with the model name actually used. */
  onModel?: (model: string) => void;
  /** Hard ceiling on tool-call iterations. */
  maxIterations?: number;
}

export async function runConversation(opts: RunOptions): Promise<ChatTurn[]> {
  const { provider } = opts;
  if (provider === 'anthropic') return runAnthropic(opts);
  return runOpenAI(opts);
}

const ANTHROPIC_MODEL = 'claude-haiku-4-5';
const OPENAI_MODEL = 'gpt-5.2';
const ANTHROPIC_FALLBACK = 'claude-3-5-haiku-latest';
const OPENAI_FALLBACK = 'gpt-4o-mini';

// ─── Anthropic ───
async function runAnthropic(opts: RunOptions): Promise<ChatTurn[]> {
  const display: ChatTurn[] = [...opts.history, { role: 'user', text: opts.userMessage }];

  // Build provider-specific message list from displayed history (text-only) plus
  // the new user turn. Tool roundtrips happen within this call only.
  const messages: Array<{ role: 'user' | 'assistant'; content: unknown }> = [];
  for (const turn of opts.history) {
    messages.push({ role: turn.role, content: turn.text });
  }
  messages.push({ role: 'user', content: opts.userMessage });

  const max = opts.maxIterations ?? 6;
  let assistantText = '';

  for (let i = 0; i < max; i += 1) {
    const data = await callAnthropic(opts.apiKey, messages, opts.onModel);
    const blocks = (data.content ?? []) as Array<Record<string, unknown>>;
    messages.push({ role: 'assistant', content: blocks });

    let toolUseFound = false;
    const toolResults: Array<Record<string, unknown>> = [];
    for (const block of blocks) {
      if (block.type === 'text' && typeof block.text === 'string') {
        assistantText += block.text;
        opts.onText(block.text);
      }
      if (block.type === 'tool_use') {
        toolUseFound = true;
        const name = String(block.name);
        const input = block.input ?? {};
        const result = executeTool(name, input);
        opts.onTool({
          name,
          ok: result.ok,
          detail: result.ok ? undefined : result.error,
        });
        toolResults.push({
          type: 'tool_result',
          tool_use_id: block.id,
          content: JSON.stringify(result.ok ? result.result : { error: result.error }),
          is_error: !result.ok,
        });
      }
    }

    if (!toolUseFound) break;
    messages.push({ role: 'user', content: toolResults });
  }

  display.push({ role: 'assistant', text: assistantText.trim() || '(완료)' });
  return display;
}

async function callAnthropic(
  apiKey: string,
  messages: Array<{ role: string; content: unknown }>,
  onModel?: (m: string) => void,
): Promise<{ content: unknown[] }> {
  const body = (model: string) => ({
    model,
    max_tokens: 2048,
    system: SYSTEM_PROMPT,
    tools: tools.map((t) => ({
      name: t.name,
      description: t.description,
      input_schema: t.input_schema,
    })),
    messages,
  });

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
    'anthropic-version': '2023-06-01',
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  let res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers,
    body: JSON.stringify(body(ANTHROPIC_MODEL)),
  });
  if (!res.ok && res.status === 404) {
    res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers,
      body: JSON.stringify(body(ANTHROPIC_FALLBACK)),
    });
    onModel?.(ANTHROPIC_FALLBACK);
  } else {
    onModel?.(ANTHROPIC_MODEL);
  }
  if (!res.ok) {
    const text = await safeText(res);
    throw new Error(`Anthropic ${res.status}: ${text}`);
  }
  return res.json();
}

// ─── OpenAI ───
async function runOpenAI(opts: RunOptions): Promise<ChatTurn[]> {
  const display: ChatTurn[] = [...opts.history, { role: 'user', text: opts.userMessage }];

  const messages: Array<Record<string, unknown>> = [{ role: 'system', content: SYSTEM_PROMPT }];
  for (const turn of opts.history) {
    messages.push({ role: turn.role, content: turn.text });
  }
  messages.push({ role: 'user', content: opts.userMessage });

  const max = opts.maxIterations ?? 6;
  let assistantText = '';

  for (let i = 0; i < max; i += 1) {
    const data = await callOpenAI(opts.apiKey, messages, opts.onModel);
    const choice = data.choices?.[0];
    const msg = choice?.message ?? {};
    messages.push(msg);

    if (typeof msg.content === 'string' && msg.content.length > 0) {
      assistantText += msg.content;
      opts.onText(msg.content);
    }

    const toolCalls = (msg.tool_calls ?? []) as Array<{
      id: string;
      function: { name: string; arguments: string };
    }>;
    if (!toolCalls.length) break;

    for (const tc of toolCalls) {
      const name = tc.function?.name;
      let args: unknown = {};
      try {
        args = JSON.parse(tc.function?.arguments || '{}');
      } catch {
        // ignore
      }
      const result = executeTool(name, args);
      opts.onTool({
        name,
        ok: result.ok,
        detail: result.ok ? undefined : result.error,
      });
      messages.push({
        role: 'tool',
        tool_call_id: tc.id,
        content: JSON.stringify(result.ok ? result.result : { error: result.error }),
      });
    }
  }

  display.push({ role: 'assistant', text: assistantText.trim() || '(완료)' });
  return display;
}

async function callOpenAI(
  apiKey: string,
  messages: Array<Record<string, unknown>>,
  onModel?: (m: string) => void,
): Promise<{ choices: Array<{ message: Record<string, unknown> }> }> {
  const body = (model: string) => ({
    model,
    messages,
    tools: tools.map((t) => ({
      type: 'function',
      function: { name: t.name, description: t.description, parameters: t.input_schema },
    })),
  });
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${apiKey}`,
  };

  let res = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers,
    body: JSON.stringify(body(OPENAI_MODEL)),
  });
  if (!res.ok && (res.status === 404 || res.status === 400)) {
    res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers,
      body: JSON.stringify(body(OPENAI_FALLBACK)),
    });
    onModel?.(OPENAI_FALLBACK);
  } else {
    onModel?.(OPENAI_MODEL);
  }
  if (!res.ok) {
    const text = await safeText(res);
    throw new Error(`OpenAI ${res.status}: ${text}`);
  }
  return res.json();
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return res.statusText;
  }
}
